// Shared loader for the tenant's configurable Change-Management value sets
// (`/api/config/change`): change statuses, types, and risks. These used to be
// hard-coded constants; they are now tenant-editable (Settings → Change
// Management). While the fetch is in flight — or if it fails — we fall back to
// the built-in defaults so the queue/board always renders.
//
// Consumed two ways:
//   • useChangeConfig() — fetches and returns the index (used at a page root).
//   • <ChangeConfigProvider> + useChangeOptions() — provide once per page so
//     deep components (status/risk badges, move selects) resolve labels/colors
//     without prop-drilling. Outside a provider useChangeOptions() yields the
//     built-in defaults, so isolated renders still work.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { getChangeConfig } from "./api";
import type { ChangeOption } from "./types";
import { useApi } from "./useApi";

/** Built-in defaults — kept in sync with the edge + CHANGE_DEFAULTS (config-db). */
export const CHANGE_STATUS_DEFAULTS: ChangeOption[] = [
  { code: "draft", label: "Draft" },
  { code: "in_review", label: "In review" },
  { code: "approved", label: "Approved" },
  { code: "scheduled", label: "Scheduled" },
  { code: "implemented", label: "Implemented" },
  { code: "closed", label: "Closed" },
  { code: "rejected", label: "Rejected" },
];
export const CHANGE_TYPE_DEFAULTS: ChangeOption[] = [
  { code: "standard", label: "Standard" },
  { code: "normal", label: "Normal" },
  { code: "emergency", label: "Emergency" },
];
export const CHANGE_RISK_DEFAULTS: ChangeOption[] = [
  { code: "low", label: "Low" },
  { code: "medium", label: "Medium" },
  { code: "high", label: "High" },
];
export const CHANGE_SCHEDULE_DEFAULTS: ChangeOption[] = [
  { code: "daily", label: "Daily" },
  { code: "weekly", label: "Weekly" },
  { code: "bi_weekly", label: "Bi-Weekly" },
  { code: "monthly", label: "Monthly" },
  { code: "quarterly", label: "Quarterly" },
  { code: "every_semester", label: "Every Semester" },
  { code: "annually", label: "Annually" },
];
export const CHANGE_IMPACT_DEFAULTS: ChangeOption[] = [
  { code: "high", label: "High" },
  { code: "moderate", label: "Moderate" },
  { code: "low", label: "Low" },
];
export const CHANGE_URGENCY_DEFAULTS: ChangeOption[] = [
  { code: "high", label: "High" },
  { code: "moderate", label: "Moderate" },
  { code: "low", label: "Low" },
];

export interface ChangeConfigIndex {
  statuses: ChangeOption[];
  types: ChangeOption[];
  risks: ChangeOption[];
  schedules: ChangeOption[];
  impacts: ChangeOption[];
  urgencies: ChangeOption[];
  /** Resolve a code → its label within a given list (falls back to the code). */
  labelOf: (list: ChangeOption[], code: string | null | undefined) => string;
  /** Resolve a code → its color within a given list (null when none/unknown). */
  colorOf: (list: ChangeOption[], code: string | null | undefined) => string | null;
  loading: boolean;
}

const DEFAULT_INDEX: ChangeConfigIndex = {
  statuses: CHANGE_STATUS_DEFAULTS,
  types: CHANGE_TYPE_DEFAULTS,
  risks: CHANGE_RISK_DEFAULTS,
  schedules: CHANGE_SCHEDULE_DEFAULTS,
  impacts: CHANGE_IMPACT_DEFAULTS,
  urgencies: CHANGE_URGENCY_DEFAULTS,
  labelOf: (list, code) => list.find((o) => o.code === code)?.label ?? code ?? "—",
  colorOf: (list, code) => list.find((o) => o.code === code)?.color ?? null,
  loading: false,
};

/** Load the tenant's change value sets once and expose them + resolvers. */
export function useChangeConfig(): ChangeConfigIndex {
  const { data, loading } = useApi((signal) => getChangeConfig(signal), []);
  return useMemo<ChangeConfigIndex>(() => {
    const cfg = data?.config;
    return {
      statuses: cfg?.statuses?.length ? cfg.statuses : CHANGE_STATUS_DEFAULTS,
      types: cfg?.types?.length ? cfg.types : CHANGE_TYPE_DEFAULTS,
      risks: cfg?.risks?.length ? cfg.risks : CHANGE_RISK_DEFAULTS,
      schedules: cfg?.schedules?.length ? cfg.schedules : CHANGE_SCHEDULE_DEFAULTS,
      impacts: cfg?.impacts?.length ? cfg.impacts : CHANGE_IMPACT_DEFAULTS,
      urgencies: cfg?.urgencies?.length ? cfg.urgencies : CHANGE_URGENCY_DEFAULTS,
      labelOf: DEFAULT_INDEX.labelOf,
      colorOf: DEFAULT_INDEX.colorOf,
      loading,
    };
  }, [data, loading]);
}

const ChangeConfigContext = createContext<ChangeConfigIndex>(DEFAULT_INDEX);

/** Fetch the value sets once and share them with the subtree. Put this at a
 *  change-management page root; descendants read via useChangeOptions(). */
export function ChangeConfigProvider({ children }: { children: ReactNode }) {
  const index = useChangeConfig();
  return <ChangeConfigContext.Provider value={index}>{children}</ChangeConfigContext.Provider>;
}

/** Read the shared change value sets (defaults if no provider is mounted). */
export function useChangeOptions(): ChangeConfigIndex {
  return useContext(ChangeConfigContext);
}
